import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useSWRConfig } from 'swr';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { EngineService } from '@/services/engine';
import { CellChange } from '@/types/table';
import { CameraTableRow } from '../types';
import { cleanupAfterSourceDeletion } from '@/stores';
import { revalidateIdentityViews } from '@/utils/swr';
import {
    isReportsListCacheKey,
    isSourcesCacheKey,
} from '@/constants/cache-keys';
import { revalidateSourceFolders } from '@/hooks';
import {
    revalidateAlertUnitCapacity,
    revalidateOrganizationConfiguration,
} from '@/features/SettingsView/hooks/useConfiguration';

const CameraConfigurationKeys = {
    streamUrl: 'stream_url',
    username: 'username',
    password: 'password',
} as const;

type CameraOverrides = {
    [K in (typeof CameraConfigurationKeys)[keyof typeof CameraConfigurationKeys]]?: string;
};

export interface UseCameraActionsReturn {
    handleEdit: (camera: CameraTableRow) => void;
    handleDelete: (camera: CameraTableRow) => Promise<void>;
    handleRefresh: (sourceId: string) => Promise<void>;
    saveChanges: () => Promise<void>;
    isDeleting: boolean;
    isSaving: boolean;
    deleteConfirmCamera: CameraTableRow | null;
    setDeleteConfirmCamera: (camera: CameraTableRow | null) => void;
    thumbnailKeys: Record<string, number>;
}

export function useCameraActions(
    onSuccess?: () => void,
    pendingChanges: CellChange<CameraTableRow>[] = [],
): UseCameraActionsReturn {
    const { t } = useTranslation(I18nNamespace.CAMERAS);
    const { mutate } = useSWRConfig();
    const [isDeleting, setIsDeleting] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [deleteConfirmCamera, setDeleteConfirmCamera] =
        useState<CameraTableRow | null>(null);
    const [thumbnailKeys, setThumbnailKeys] = useState<Record<string, number>>(
        {},
    );

    const handleEdit = useCallback((camera: CameraTableRow) => {}, []);

    const handleDelete = useCallback(
        async (camera: CameraTableRow) => {
            if (isDeleting) return;

            setIsDeleting(true);
            try {
                await EngineService.deleteSource(camera.sourceId);
                cleanupAfterSourceDeletion(camera.sourceId);
                await revalidateIdentityViews();
                await revalidateSourceFolders();
                await mutate(isSourcesCacheKey);
                await mutate(isReportsListCacheKey);
                await revalidateOrganizationConfiguration();
                await revalidateAlertUnitCapacity();

                toast.success(t('toast.deleteSuccess', { name: camera.name }));
                setDeleteConfirmCamera(null);
                onSuccess?.();
            } catch (error) {
                console.error('Failed to delete camera:', error);
                toast.error(t('toast.deleteError'));
            } finally {
                setIsDeleting(false);
            }
        },
        [isDeleting, mutate, onSuccess, t],
    );

    const refreshValidation = useCallback(
        async (
            sourceId: string,
            overrides?: {
                stream_url?: string;
                username?: string;
                password?: string;
            },
        ) => {
            mutate(
                ['camera-validation', sourceId],
                (current) => ({ ...current, isRefreshing: true }),
                { revalidate: false },
            );

            const result = await EngineService.validateCameraBySourceId(
                sourceId,
                overrides,
            );

            mutate(
                ['camera-validation', sourceId],
                {
                    live: result.isValid,
                    errorType: result.isValid ? undefined : result.errorType,
                    lastCheckedAt: new Date(),
                    isRefreshing: false,
                },
                { revalidate: false },
            );

            setThumbnailKeys((prev) => ({
                ...prev,
                [sourceId]: Date.now(),
            }));
        },
        [mutate],
    );

    const handleRefresh = useCallback(
        async (sourceId: string) => {
            const overrides = pendingChanges
                .filter(
                    (c) =>
                        c.rowId === sourceId &&
                        c.accessor in CameraConfigurationKeys,
                )
                .reduce<CameraOverrides>((acc, change) => {
                    if (
                        change.accessor === CameraConfigurationKeys.password &&
                        !change.newValue
                    ) {
                        return acc;
                    }
                    const key =
                        CameraConfigurationKeys[
                            change.accessor as keyof typeof CameraConfigurationKeys
                        ];
                    return { ...acc, [key]: String(change.newValue) };
                }, {});

            await refreshValidation(
                sourceId,
                Object.keys(overrides).length > 0 ? overrides : undefined,
            );
        },
        [pendingChanges, refreshValidation],
    );

    const saveChanges = useCallback(async () => {
        const changesByRow = new Map<string, CellChange<CameraTableRow>[]>();

        for (const change of pendingChanges) {
            if (
                change.accessor === CameraConfigurationKeys.password &&
                !change.newValue
            )
                continue;
            const existing = changesByRow.get(change.rowId) || [];
            changesByRow.set(change.rowId, [...existing, change]);
        }

        if (changesByRow.size === 0) return;

        setIsSaving(true);

        try {
            await Promise.all(
                Array.from(changesByRow.entries()).map(
                    async ([rowId, rowChanges]) => {
                        const payload: Record<string, string | undefined> = {};
                        for (const change of rowChanges) {
                            payload[String(change.accessor)] = String(
                                change.newValue,
                            );
                        }

                        await EngineService.updateCamera(rowId, payload);
                        await refreshValidation(rowId);
                    },
                ),
            );

            toast.success(t('table.saveSuccess'));
            onSuccess?.();
        } finally {
            setIsSaving(false);
        }
    }, [pendingChanges, refreshValidation, onSuccess, t]);

    return {
        handleEdit,
        handleDelete,
        handleRefresh,
        saveChanges,
        isDeleting,
        isSaving,
        deleteConfirmCamera,
        setDeleteConfirmCamera,
        thumbnailKeys,
    };
}
